import sortBy from 'lodash/sortBy';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import merge from 'lodash/merge';
import { LOADING_STATES } from '@/constants';
import { formatValueForSchema, largestRemainderRound } from '@/utils/formatters';
import { BuildingBlockActions } from '@pendo/services/BuildingBlocks';
import { isCrossApp } from '@pendo/services/CrossAppGuides';
import { getUiElementType } from '@pendo/one-ui-shared';
import { i18n_t as $t } from '@utils/i18n';

const { actionTypes } = BuildingBlockActions;

export function getTabResolvedAggStatus (data) {
    return data && data.length ? LOADING_STATES.RESOLVED : LOADING_STATES.EMPTY;
}

export function getElementsTableColumns ({ grouped, elementActionColumnWidth, csv = false, interactive = false }) {
    const includeGuideStepName = !grouped || csv;
    const includePercentClicksOnStep = grouped || csv;

    return [
        {
            prop: 'uiElementText',
            label: $t('Name'),
            minWidth: 260,
            sortable: true,
            fixed: true,
        },
        csv && {
            prop: 'uiElementId',
            label: $t('guideElementsTable.Element Id'),
        },
        {
            prop: 'uiElementType',
            label: $t('guideElementsTable.Element Type'),
            width: 180,
            sortable: true,
        },
        csv && {
            prop: 'guideStepId',
            label: $t('guideElementsTable.Step Id'),
        },
        includeGuideStepName && {
            prop: 'guideStepName',
            label: $t('guideElementsTable.Step Name'),
            sortable: true,
        },
        {
            prop: 'uiElementActions',
            label: $t('guideElementsTable.Action'),
            width: elementActionColumnWidth,
            formatter: (row) => row.uiElementActions.map((action) => action.action).join(' & '),
        },
        {
            prop: 'numClicks',
            type: interactive ? 'link' : null,
            label: $t('guideElementsTable.Number of Clicks'),
            sortable: true,
        },
        includePercentClicksOnStep && {
            prop: 'percentClicksOnStep',
            label: $t('guideElementsTable.percentOfClicks'),
            sortable: true,
            formatter: (row) => formatValueForSchema(row.percentClicksOnStep, 'percentage'),
        },
        csv && {
            prop: 'uiElementStatus',
            label: $t('guideElementsTable.Element Status'),
            formatter: (row) => {
                if (row.isDeleted) {
                    return 'Deleted';
                }

                return 'Active';
            },
        },
    ].filter(Boolean);
}

export function getStepName (stepId, guide) {
    const step = guide.steps.find((step) => step.id === stepId);

    return get(step, 'name');
}

export function getStepIndex (stepId, guide) {
    return guide.steps.findIndex((step) => step.id === stepId);
}

export function formatUiElementTextForDisplay (uiElementText, uiElementId) {
    if (!uiElementText) {
        return uiElementId || 'Unknown';
    }

    if (uiElementText === '×') {
        return 'Close Button';
    }

    return uiElementText;
}

export function formatStepNameForDisplay (stepIndex, stepName) {
    if (stepIndex === -1) {
        return $t('guideElementsTable.Deleted Step');
    }

    if (!stepName) {
        return $t('guideElementsTable.Step {stepNumber} - Unnamed', { stepNumber: stepIndex + 1 });
    }

    return stepName;
}

export function formatPercentClicksOnStep (rows, showDeletedElements = false) {
    const totalClicksByStep = {};
    rows.forEach((row) => {
        if (!showDeletedElements && row.isDeleted) return;
        if (totalClicksByStep[row.guideStepId]) {
            totalClicksByStep[row.guideStepId] += row.numClicks;
        } else {
            totalClicksByStep[row.guideStepId] = row.numClicks;
        }
    });
    rows.forEach((row) => {
        row.percentClicksOnStep = row.numClicks > 0 ? (row.numClicks / totalClicksByStep[row.guideStepId]) * 100 : 0;
    });
    const stepUiElementsMap = rows.reduce((acc, row) => {
        if (!showDeletedElements && row.isDeleted) return acc;
        const { guideStepId, uiElementId, percentClicksOnStep } = row;
        if (acc[guideStepId]) {
            acc[guideStepId][uiElementId] = percentClicksOnStep;
        } else {
            acc[guideStepId] = { [uiElementId]: percentClicksOnStep };
        }

        return acc;
    }, {});
    Object.keys(stepUiElementsMap).forEach((guideStepId) => {
        let formattedPercentClicksOnStep = Object.values(stepUiElementsMap[guideStepId]);
        formattedPercentClicksOnStep = largestRemainderRound(formattedPercentClicksOnStep, 100, 1);
        Object.keys(stepUiElementsMap[guideStepId]).forEach((uiElementId, i) => {
            stepUiElementsMap[guideStepId][uiElementId] = formattedPercentClicksOnStep[i];
        });
    });
    rows.forEach((row) => {
        if (!showDeletedElements && row.isDeleted) return;
        row.percentClicksOnStep = stepUiElementsMap[row.guideStepId][row.uiElementId];
    });

    return rows;
}

export function completeGuideStepIdIfMissing (rows, currentElementData) {
    return rows.map((row) => {
        if (row.guideStepId) return row;
        const element = currentElementData.find((elem) => elem.uiElementId === row.uiElementId);

        if (element && element.guideStepId) {
            row.guideStepId = element.guideStepId;
        }

        return row;
    });
}

export function processGuideElementsAggResponse (
    rows,
    guide,
    currentElementData,
    showDeletedElements,
    guideList,
    isCrossApp,
    steps,
) {
    rows = completeGuideStepIdIfMissing(rows, currentElementData);
    rows = formatPercentClicksOnStep(rows, showDeletedElements);
    const mergedData = merge(keyBy(rows, 'uiElementId'), keyBy(currentElementData, 'uiElementId'));

    const data = Object.values(mergedData).map((row) => {
        const stepName = row.guideStepName || getStepName(row.guideStepId, guide);
        const stepIndex = row.guideStepIndex || getStepIndex(row.guideStepId, guide);
        const uiElementType = row.isDeleted ? getUiElementType(row.uiElementId, row.uiElementType) : row.uiElementType;

        if (isCrossApp) {
            const appId = getAppIdForStep(stepIndex, steps);
            const application = getAppNameForStep(stepIndex, steps);

            row = {
                ...row,
                appId,
                application,
            };
        }

        return {
            ...row,
            numClicks: row.numClicks || 0,
            percentClicksOnStep: row.percentClicksOnStep || 0,
            uiElementType,
            uiElementTypeIcon: getUiElementTypeIcon(uiElementType),
            guideStepName: formatStepNameForDisplay(stepIndex, stepName),
            guideStepIndex: stepIndex,
            uiElementText: formatUiElementTextForDisplay(row.uiElementText),
            uiElementActions: getUiElementActions(row, guide, guideList),
        };
    });

    return sortBy(data, 'guideStepIndex');
}

export function processGuideElementsAggDropdown (rows, currentElementData) {
    const mergedData = merge(keyBy(rows, 'uiElementId'), keyBy(currentElementData, 'uiElementId'));

    return Object.values(mergedData).map((row) => {
        const elementText = formatUiElementTextForDisplay(row.uiElementText, row.uiElementId);

        return {
            guideElementId: row.uiElementId,
            guideStepId: row.guideStepId,
            name: row.isDeleted ? `Deleted Element (${elementText})` : elementText,
            icon: { type: getUiElementTypeIcon(row.uiElementType) },
        };
    });
}

export function getUiElementTypeIcon (elementType) {
    return {
        'Button': 'rectangle',
        'Close Button': 'x',
        'Dismiss Button': 'x',
        'Link': 'link',
        'Task Item': 'list-checks',
    }[elementType];
}

export function parseActions (actions) {
    try {
        return JSON.parse(actions);
    } catch {
        return actions;
    }
}

export function getUiElementActions (row, guide, guideList) {
    const actions = parseActions(row.uiElementActions || '[]');

    if (actions && actions.length) {
        return actions.reduce((acc, action) => {
            switch (action.action) {
                case actionTypes.submitPoll:
                    acc.push(
                        processElementAction({ action: actionTypes.submitPoll }, guide),
                        processElementAction({ action: actionTypes.advanceGuide }, guide),
                    );
                    break;
                case actionTypes.submitPollAndGoToStep:
                    acc.push(
                        processElementAction({ action: actionTypes.submitPoll }, guide),
                        processElementAction({ ...action, action: actionTypes.goToStep }, guide),
                    );
                    break;
                case actionTypes.confirmation:
                    acc.push(
                        processElementAction({ action: actionTypes.confirmation }, guide),
                        processElementAction({ action: actionTypes.advanceGuide }, guide),
                    );
                    break;
                default:
                    acc.push(processElementAction(action, guide, guideList));
            }

            return acc;
        }, []);
    }

    return [{ action: 'Unknown' }];
}

export function processElementAction (action, guide, guideList) {
    switch (action.action) {
        case actionTypes.openLinkWithoutClosing:
        case actionTypes.openLink:
            return { action: 'External URL', icon: 'external-link' };
        case actionTypes.goToStep: {
            const stepId = action.guideStepId
                ? action.guideStepId
                : get(
                    action.parameters.find((param) => param.name === 'goToStepId'),
                    'value',
                );

            const stepNumber = getStepIndex(stepId, guide) + 1 || 'Deleted Step';

            return { action: `Go to Step (${stepNumber})`, icon: 'fast-forward' };
        }
        case actionTypes.guideSnoozed:
            return { action: 'Snooze Guide', icon: 'clock' };
        case 'showGuide':
        case actionTypes.launchGuide: {
            let actionText = 'Launch Guide';
            const guideId = action.guideId || action.parameters[0]?.value;
            if (guideId) {
                const launchGuide = guideList.find((guide) => guide.id === guideId);
                actionText += ` – ${launchGuide ? launchGuide.name : '[DELETED GUIDE]'}`;
            }

            return { action: actionText, icon: 'guide' };
        }
        case actionTypes.advanceGuide:
            return { action: 'Next Step', icon: 'skip-forward' };
        case actionTypes.confirmation:
            return { action: 'Confirm Action', icon: 'confirmation' };
        case actionTypes.previousStep:
            return { action: 'Previous Step', icon: 'rewind' };
        case actionTypes.submitPoll:
            return { action: 'Submit Poll', icon: 'metrics' };
        case actionTypes.dismissGuide:
            return { action: 'Dismiss Guide', icon: 'x-circle' };
        case actionTypes.openRatingDialog:
            return { action: 'Mobile Rating', icon: 'rating-scale' };
        case actionTypes.automation:
            return { action: 'Automation', icon: 'automation' };
        case actionTypes.conditionalSplit:
            return { action: 'Conditional split', icon: 'or' };
        default:
            return { action: 'Unknown' };
    }
}

export function getGroupByLabel (guide) {
    return isCrossApp(guide) ? $t('guideElementsTable.Group by step and app') : $t('guideElementsTable.Group by step');
}

export function getAppIdForStep (stepIndex, steps) {
    return get(steps[stepIndex], 'appId', 'deleted');
}

export function getAppNameForStep (stepIndex, steps) {
    return get(steps[stepIndex], 'appName', 'deleted');
}
